import { useState, useEffect } from 'react';
import { X, Star, Trash2, Eye } from 'lucide-react';
import { useUserRating, useRateMedia, useDeleteRating } from '../hooks/useRatings';
import { useUserLibrary } from '../contexts/UserLibraryContext';
import { useToast } from './ui/Toast';
import { getPosterUrl } from '../utils/helpers';
import type { MediaItem } from '../types/movie';

interface RatingModalProps {
  movie: MediaItem;
  isOpen: boolean;
  onClose: () => void;
  onRated?: (rating: number) => void;
}

const RATING_LABELS: Record<number, string> = {
  1: 'Terrible',
  2: 'Very Bad',
  3: 'Bad',
  4: 'Below Average',
  5: 'Average',
  6: 'Above Average',
  7: 'Good',
  8: 'Great',
  9: 'Excellent',
  10: 'Masterpiece',
};

export function RatingModal({ movie, isOpen, onClose, onRated }: RatingModalProps) {
  const mediaType = movie.media_type || 'movie';

  // React Query hooks for ratings
  const { rating: existingRating, isLoading: loadingRating } = useUserRating(movie.id, mediaType);
  const { rateMedia, isLoading: savingRate } = useRateMedia();
  const { deleteRating, isLoading: savingDelete } = useDeleteRating();

  const { isWatched, toggleWatched } = useUserLibrary();
  const { toast } = useToast();

  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [addToWatched, setAddToWatched] = useState(true);

  const saving = savingRate || savingDelete;
  const displayRating = hoverRating ?? selectedRating;
  const isInWatched = isWatched(movie.id, mediaType);

  // Sync selectedRating with existingRating from React Query
  useEffect(() => {
    if (isOpen && existingRating !== null) {
      setSelectedRating(existingRating);
    }
  }, [isOpen, existingRating]);

  // Reset hover state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHoverRating(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleRate = async () => {
    if (!selectedRating) return;

    try {
      // Save rating (with title/poster for activity creation)
      const result = await rateMedia(
        movie.id,
        mediaType,
        selectedRating,
        movie.title,
        movie.poster_path
      );

      if (result) {
        let watchedAddFailed = false;

        // Add to watched list if checkbox is checked
        if (addToWatched && !isInWatched) {
          const mediaYear = movie.release_date
            ? parseInt(movie.release_date.slice(0, 4), 10)
            : undefined;

          try {
            await toggleWatched(
              movie.id,
              mediaType,
              movie.title,
              movie.poster_path ?? undefined,
              mediaYear
            );
            // Returns null if superseded - that's ok, don't show error
          } catch (watchedErr) {
            // toggleWatched throws on failure - show info, not error
            console.error('Error marking as watched:', watchedErr);
            toast.info('Rating saved, but couldn\'t mark as watched');
            watchedAddFailed = true;
          }
        }

        onRated?.(selectedRating);
        onClose();

        if (!watchedAddFailed) {
          toast.success('Rating saved');
        }
      } else {
        toast.error('Something went wrong');
      }
    } catch (err) {
      console.error('Error saving rating:', err);
      toast.error('Something went wrong');
    }
  };

  const handleDeleteRating = async () => {
    try {
      const success = await deleteRating(movie.id, mediaType);
      if (success) {
        toast.success('Rating removed');
        setSelectedRating(null);
        onClose();
      } else {
        toast.error('Something went wrong');
      }
    } catch (err) {
      console.error('Error deleting rating:', err);
      toast.error('Something went wrong');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#0A0A0A]/80 backdrop-blur-sm"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }}
      />

      {/* Modal */}
      <div
        className="relative bg-[#0F1419] rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with movie info */}
        <div className="relative h-32 overflow-hidden">
          {movie.backdrop_path && (
            <img
              src={`https://image.tmdb.org/t/p/w780${movie.backdrop_path}`}
              alt=""
              className="w-full h-full object-cover opacity-40"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent" />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 bg-[#0A0A0A]/50 rounded-full text-white hover:bg-[#0A0A0A]/70 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Movie info */}
          <div className="absolute bottom-3 left-3 right-3 flex gap-3">
            {movie.poster_path && (
              <img
                src={getPosterUrl(movie.poster_path, 'small')}
                alt={movie.title}
                className="w-12 h-18 rounded object-cover shadow-lg"
              />
            )}
            <div className="flex-1 min-w-0">
              <h2 className="text-white font-semibold text-lg line-clamp-1">
                {movie.title}
              </h2>
              <p className="text-slate-400 text-sm">
                {movie.release_date?.slice(0, 4)}
              </p>
            </div>
          </div>
        </div>

        {/* Rating section */}
        <div className="p-6">
          {loadingRating ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500" />
            </div>
          ) : (
            <>
              {/* Rating label - fixed height to prevent layout shift */}
              <div className="text-center mb-4 h-20 flex flex-col justify-center">
                <p className="text-slate-400 text-sm mb-1">Your Rating</p>
                {displayRating ? (
                  <div>
                    <p className="text-2xl font-bold">
                      <span className="text-yellow-500">{displayRating}</span>
                      <span className="text-slate-500">/10</span>
                    </p>
                    <p className="text-sm font-normal text-slate-400 mt-1">
                      {RATING_LABELS[displayRating]}
                    </p>
                  </div>
                ) : (
                  <p className="text-2xl font-bold text-slate-500">Tap to rate</p>
                )}
              </div>

              {/* Star rating */}
              <div
                className="flex justify-center gap-1 mb-6"
                onMouseLeave={() => setHoverRating(null)}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => setSelectedRating(rating)}
                    onMouseEnter={() => setHoverRating(rating)}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-7 h-7 transition-colors ${
                        (displayRating && rating <= displayRating)
                          ? 'fill-yellow-500 text-yellow-500'
                          : 'text-slate-600 hover:text-slate-500'
                      }`}
                    />
                  </button>
                ))}
              </div>

              {/* Add to Watched checkbox */}
              {!isInWatched && (
                <label className="flex items-center gap-3 text-sm text-slate-300 mb-6 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addToWatched}
                    onChange={(e) => setAddToWatched(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-600 bg-[#1A1F26] text-teal-500 focus:ring-teal-500 focus:ring-offset-slate-900"
                  />
                  <Eye className="w-4 h-4 text-slate-400" />
                  <span>Also mark as watched</span>
                </label>
              )}

              {isInWatched && (
                <p className="flex items-center gap-2 text-sm text-teal-400 mb-6">
                  <Eye className="w-4 h-4" />
                  <span>Already in your Watched list</span>
                </p>
              )}

              {/* Action buttons */}
              <div className="flex gap-3">
                {selectedRating && (
                  <button
                    onClick={handleDeleteRating}
                    disabled={saving}
                    className="px-4 py-3 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                    title="Remove rating"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={handleRate}
                  disabled={!selectedRating || saving}
                  className="flex-1 px-6 py-3 rounded-lg bg-teal-500 text-white font-medium hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Rating'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
